import {
  ApplicationRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
} from '@angular/core';
import { EMPTY, Observable, Subject, concatMap, forkJoin, race, timer } from 'rxjs';
import { bufferCount, catchError, delay, map, take, tap } from 'rxjs/operators';
import { IReport } from '@interfaces/index';
import { ReportAssetsRenderedService } from './report-assets-rendered.service';
import { ReportComponent } from './report.component';
import { ChartRenderedService } from '@ui-components/services';

// report.component.html mounts exactly one score-summary component, branching on
// templateId: app-coachee-score-summary for CoachingStyles/CoachingStylesV2, else
// app-score-summary — never both. Each of those two components unconditionally
// renders two <lib-chart> instances (canvasId "CoachingStyle" and "DirectIndirect"),
// which is where the two rendered() signals come from, not from two summary
// components. ChartRenderedService.rendered$ is a shared root Subject that fires
// once per chart's animation completing, so waiting on the first emission (the
// previous behaviour) resolved before the second chart had drawn — the capture
// then raced the second chart's own onComplete, and the download pipeline stalled
// waiting on a signal that had already been consumed by the first wait. Waiting
// for both fixes the cause rather than the timing window.
//
// This count is not enforced anywhere — it lives implicitly in the two summary
// components' templates (projects/ui-components/src/report/score-summary/
// score-summary.component.html and
// projects/ui-components/src/report/coachee-report/coachee-score-summary/
// coachee-score-summary.component.html) and in report.component.html's branch
// that mounts one or the other. A future template that mounts a third summary
// component, or a chart count that changes in either of the two above, must
// update CHARTS_PER_REPORT to match or this wait silently falls back to the
// 3000ms timeout on every render.
const CHARTS_PER_REPORT = 2;
// The chart-mounted signal is not guaranteed to fire for every report shape
// (see the plan's inherited-race note), so the wait is bounded and the render
// proceeds on whichever of rendered$/timeout wins first — deliberately no
// better and no worse than the existing download sites.
//
// FrontPageComponent's two images (logo, header) load asynchronously via
// fetch(), entirely independent of the chart wait — nothing previously waited
// on them, so a capture could run before their *ngIf-gated <img> elements
// existed, producing a smaller, silently-incomplete HTML capture (TK-080
// AC3). ReportAssetsRenderedService.ready$ closes that gap; it is awaited in
// parallel with the chart wait below, bounded by the same timeout, so the
// worst-case total wait is unchanged.
const CHART_RENDER_TIMEOUT_MS = 3000;
// After flipping `download` to swap each <canvas> for a captured <img>, one
// extra change-detection pass gives the DOM time to reflect the swap before
// the innerHTML is captured.
const DOWNLOAD_FLIP_SETTLE_MS = 500;

/**
 * Shared, process-wide off-screen report renderer used by every download site
 * (admin list and the assessments dashboard). Renders are serialised — exactly
 * one at a time — because ChartRenderedService.rendered$ is a single root
 * Subject: two concurrent renders would cross-consume each other's chart
 * signals and at least one would fall back to the timeout, capturing before
 * its charts had drawn. A render queued behind another simply waits its turn
 * and then runs normally; it is never dropped, never rejected, and never
 * merged with another.
 */
@Injectable({
  providedIn: 'root',
})
export class ReportRenderService {

  private _queue = new Subject<{ report: IReport; subscriber: { next: (html: string) => void; error: (err: unknown) => void; complete: () => void } }>();

  constructor(
    private _app_ref: ApplicationRef,
    private _env_injector: EnvironmentInjector,
    private _chart_rendered_service: ChartRenderedService,
    private _report_assets_rendered_service: ReportAssetsRenderedService
  ) {
    this._queue.pipe(
      concatMap((job) => this._render(job.report).pipe(
        tap((html) => {
          job.subscriber.next(html);
          job.subscriber.complete();
        }),
        map(() => undefined),
        catchError((err) => {
          job.subscriber.error(err);
          return EMPTY;
        })
      ))
    ).subscribe();
  }

  renderToHtml(report: IReport): Observable<string> {
    return new Observable<string>((subscriber) => {
      this._queue.next({
        report,
        subscriber: {
          next: (html) => subscriber.next(html),
          error: (err) => subscriber.error(err),
          complete: () => subscriber.complete(),
        },
      });
      return () => undefined;
    });
  }

  private _render(report: IReport): Observable<string> {
    return new Observable<string>((subscriber) => {
      const host = this._createHost();
      document.body.appendChild(host);

      const componentRef = createComponent(ReportComponent, {
        environmentInjector: this._env_injector,
        hostElement: host,
      });
      const instance = componentRef.instance;
      instance.report = report;
      this._app_ref.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();

      const chartsReady$ = race(
        this._chart_rendered_service.rendered$.pipe(bufferCount(CHARTS_PER_REPORT), take(1)),
        timer(CHART_RENDER_TIMEOUT_MS)
      );
      const assetsReady$ = race(
        this._report_assets_rendered_service.ready$.pipe(take(1)),
        timer(CHART_RENDER_TIMEOUT_MS)
      );

      const subscription = forkJoin([chartsReady$, assetsReady$]).pipe(
        tap(() => {
          instance.download = true;
          componentRef.changeDetectorRef.detectChanges();
        }),
        delay(DOWNLOAD_FLIP_SETTLE_MS),
        tap(() => componentRef.changeDetectorRef.detectChanges()),
        map(() => {
          const documentElement = host.querySelector('#document');
          return documentElement ? documentElement.innerHTML : '';
        })
      ).subscribe({
        next: (html) => {
          subscriber.next(html);
          subscriber.complete();
        },
        error: (err) => subscriber.error(err),
      });

      return () => {
        subscription.unsubscribe();
        this._app_ref.detachView(componentRef.hostView);
        componentRef.destroy();
        host.remove();
      };
    });
  }

  private _createHost(): HTMLDivElement {
    const host = document.createElement('div');
    host.style.position = 'fixed';
    host.style.top = '0';
    host.style.left = '-20000px';
    host.style.width = `${document.documentElement.clientWidth}px`;
    host.style.visibility = 'hidden';
    host.style.pointerEvents = 'none';
    host.style.zIndex = '-1';
    return host;
  }
}
